import { ReactNode } from "react";

import { AgentChatAttachment, AgentChatMessage } from "../types";

import { useControllableState } from "./useControllableState";
import { useIncrementingId } from "./useIncrementingId";

const EMPTY_MESSAGES: readonly AgentChatMessage[] = [];

interface ChatMessagesConfig {
  readonly messages?: readonly AgentChatMessage[] | null;
}

interface ChatMessagesResult {
  readonly messages: readonly AgentChatMessage[];
  readonly appendUserMessage: (content: ReactNode, attachments?: readonly AgentChatAttachment[]) => void;
}

/**
 * Manages the conversation transcript, supporting either a controlled `messages` list or its own
 * internal state. When uncontrolled, messages the user sends are appended so they appear in the
 * discussion view; controlled callers own the list and drive updates through props.
 */
export const useChatMessages = ({ messages }: ChatMessagesConfig): ChatMessagesResult => {
  const [currentMessages, setInternalMessages, isControlled] = useControllableState<readonly AgentChatMessage[]>(messages, EMPTY_MESSAGES);
  const nextMessageId = useIncrementingId("user-message");

  /* Only self-managed conversations grow their own transcript; controlled callers append via props. */
  const appendUserMessage = (content: ReactNode, attachments?: readonly AgentChatAttachment[]) => {
    if (isControlled) return;
    const message: AgentChatMessage = {
      id: nextMessageId(),
      role: "user",
      content,
      attachments: attachments && attachments.length > 0 ? attachments : undefined,
    };
    setInternalMessages((prev) => [...prev, message]);
  };

  return {
    messages: currentMessages,
    appendUserMessage,
  };
};
